import React, { useState, useEffect, useCallback } from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { Select } from "../../../../ui/components/Select";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherInfo, FeatherX } from "@subframe/core";
import {
  Tax,
  createSemanticOverride,
  SemanticOverrideRequest,
} from "../../../../services/api";
import { AccountNameDropdown } from "../../../../shared/presentation/components";
import { GLMappingEntity } from "../../../../shared/domain/entities/GLMappingEntity";
import {
  fetchTaxSpecification,
  fetchTDSRates,
  TDSRate,
} from "../../../../shared/data/api/taxApi";

// Permissions
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import {
  checkApprovedReview,
  ChangeDetails,
} from "../../../../shared/utils/reviewWorkflow";

/**
 * ============================================================================
 * PERMISSION CHECKS DOCUMENTATION
 * ============================================================================
 *
 * Screen: Edit Line Item Modal (Credit Note)
 * File: frontend/src/features/credit/presentation/components/EditLineItemForCreditNoteComponent.tsx
 *
 * API Endpoints Used:
 * 1. GET /api/permissions/me
 *    - Purpose: Fetch all user permissions
 *    - Called: Once on component mount via usePermissions hook
 *
 * 2. POST /api/reviews (if override_gl_permanent requires review)
 *    - Purpose: Create review request for GL override
 *    - Called: When user clicks "Change it always" and review is required
 *
 * 3. GET /api/reviews/check-approved (if override_gl_permanent requires review)
 *    - Purpose: Check if GL override has approved review
 *    - Called: Before allowing override when review is required
 *
 * Request Body for POST /api/reviews:
 * {
 *   "module_code": "credit_note",
 *   "action_code": "override_gl_permanent",
 *   "entity_type": "credit_note",
 *   "entity_id": 123,
 *   "request_comments": "Request to permanently override GL",
 *   "change_details": {
 *     "old_value": "Accounts Payable",
 *     "new_value": "Office Supplies Expense",
 *     "reason": "This should be categorized as office supplies"
 *   }
 * }
 *
 * Response for POST /api/reviews:
 * {
 *   "success": true,
 *   "data": {
 *     "review_id": 456
 *   },
 *   "message": "Review request created successfully"
 * }
 *
 * Request Query Params for GET /api/reviews/check-approved:
 * ?user_id=1&entity_type=credit_note&entity_id=123&action_code=override_gl_permanent
 *
 * Response for GET /api/reviews/check-approved:
 * {
 *   "success": true,
 *   "data": {
 *     "approved": true,
 *     "review": {
 *       "id": 456,
 *       "status": "approved",
 *       "reviewed_at": "2025-01-15 10:30:00",
 *       "review_comments": "Approved for override"
 *     }
 *   }
 * }
 *
 * Interfaces Used:
 * - PermissionsMap from frontend/src/features/admin/domain/entities/PermissionEntity.ts
 * - CreateReviewRequestDTO from frontend/src/shared/utils/reviewWorkflow.ts
 * - CreateReviewResponseDTO from frontend/src/shared/utils/reviewWorkflow.ts
 * - CheckApprovedReviewResponseDTO from frontend/src/shared/utils/reviewWorkflow.ts
 *
 * Permission Checks Implemented:
 * 1. credit_note.override_gl_permanent
 *    - Checked via: getPermissionInfo(MODULE_CODES.CREDIT_NOTE, ACTION_CODES.OVERRIDE_GL_PERMANENT)
 *    - Used for: "Change it always" button enable/disable
 *    - Location: Override GL section
 *    - Review Required: Yes (if requires_review is true)
 *
 * 2. credit_note.override_gl_once
 *    - Checked via: getPermissionInfo(MODULE_CODES.CREDIT_NOTE, ACTION_CODES.OVERRIDE_GL_ONCE)
 *    - Used for: "Change it once" button enable/disable
 *    - Location: Override GL section
 *
 * Review Workflow:
 * - If override_gl_permanent requires review:
 *   1. Check for approved review via GET /api/reviews/check-approved
 *   2. If no approved review, show ReviewRequestModal to create review request
 *   3. Show success message: "Override request submitted for review"
 *   4. Only allow override if review is approved
 * ============================================================================
 */

interface EditLineItemForCreditNoteComponentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingLineItemData: any | null;
  originalAccountName: string;
  editingLineItemIndex: number | null;
  taxes?: Tax[];
  vendorName?: string;
  customerName?: string;
  isNewItem?: boolean;
  isGstEnabled?: boolean;
  isManualCreation?: boolean;
  hasLinkedDocument?: boolean;
  creditNoteId?: string | number | null; // Credit note document ID for review workflow
  onSave: () => void;
  onCancel: () => void;
  onLineItemDataChange: (data: any) => void;
}

const EditLineItemForCreditNoteComponent: React.FC<
  EditLineItemForCreditNoteComponentProps
> = ({
  open,
  onOpenChange,
  editingLineItemData,
  editingLineItemIndex,
  originalAccountName,
  taxes = [],
  customerName,
  isNewItem = false,
  isGstEnabled = true,
  isManualCreation = false,
  hasLinkedDocument = true,
  creditNoteId = null, // Credit note document ID for review workflow
  onSave,
  onCancel,
  onLineItemDataChange,
}) => {
  // Helper method for rounding
  const roundOff = (value: number): string => {
    return value.toFixed(2);
  };

  // Account change state
  const [accountChangeReason, setAccountChangeReason] = useState("");
  const [changeScope, setChangeScope] = useState<"once" | "always">("once");
  const [showAccountChangeSection, setShowAccountChangeSection] =
    useState(false);
  const [storedOriginalAccountName, setStoredOriginalAccountName] =
    useState("");

  // Selected account state for efficient handleSemanticOverride
  const [selectedAccount, setSelectedAccount] =
    useState<GLMappingEntity | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permissions
  const { getPermissionInfo } = usePermissions();
  const overridePermInfo = getPermissionInfo(
    MODULE_CODES.CREDIT_NOTE,
    ACTION_CODES.OVERRIDE_GL_PERMANENT
  );
  const canOverridePerm = overridePermInfo.granted;
  const needsReview = overridePermInfo.requiresReview;

  const overrideOnceInfo = getPermissionInfo(
    MODULE_CODES.CREDIT_NOTE,
    ACTION_CODES.OVERRIDE_GL_ONCE
  );
  const canOverrideOnce = overrideOnceInfo.granted;

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingOverrideAction, setPendingOverrideAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
  } | null>(null);

  // Check for approved review if needed
  const [hasApprovedReview, setHasApprovedReview] = useState<boolean | null>(
    null
  );
  const [isCheckingReview, setIsCheckingReview] = useState(false);

  // Check for approved review if needed (when changeScope is "always")
  useEffect(() => {
    const checkReviewStatus = async () => {
      if (
        changeScope === "always" &&
        needsReview &&
        creditNoteId &&
        hasApprovedReview === null
      ) {
        setIsCheckingReview(true);
        try {
          const result = await checkApprovedReview({
            entity_type: "credit_note",
            entity_id:
              typeof creditNoteId === "string"
                ? parseInt(creditNoteId)
                : creditNoteId,
            action_code: ACTION_CODES.OVERRIDE_GL_PERMANENT,
          });
          setHasApprovedReview(result.data.approved);
        } catch (error) {
          console.error("Failed to check review status:", error);
          setHasApprovedReview(false);
        } finally {
          setIsCheckingReview(false);
        }
      } else if (changeScope !== "always" || !needsReview) {
        setHasApprovedReview(true); // No review needed for "once" or if review not required
      }
    };

    checkReviewStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeScope, needsReview, creditNoteId]); // hasApprovedReview intentionally excluded

  // Tax state - dynamically fetched based on tax_name
  const [availableTaxes, setAvailableTaxes] = useState<Tax[]>(taxes);
  const [taxesLoading, setTaxesLoading] = useState(false);

  // TDS state
  const [tdsRates, setTdsRates] = useState<TDSRate[]>([]);
  const [tdsLoading, setTdsLoading] = useState(false);

  // Set original account name when modal opens
  useEffect(() => {
    if (open) {
      setStoredOriginalAccountName(originalAccountName);
      setShowAccountChangeSection(false);
      setAccountChangeReason("");
      setChangeScope("once");
      setError(null);
    }
  }, [open, originalAccountName]);

  // Fetch taxes only when modal opens or GST enabled changes
  // Do NOT re-fetch when tax_name changes (tax is already selected from available taxes)
  useEffect(() => {
    const loadTaxes = async () => {
      if (!open || !editingLineItemData || !isGstEnabled) {
        // Use default taxes if modal is closed or GST is disabled
        setAvailableTaxes(taxes);
        return;
      }

      // For manual creation, fetch both inter and intra taxes (only once when modal opens)
      if (isManualCreation) {
        try {
          setTaxesLoading(true);
          // Fetch both inter and intra taxes
          const [interResponse, intraResponse] = await Promise.all([
            fetchTaxSpecification("inter"),
            fetchTaxSpecification("intra"),
          ]);

          const allTaxes: Tax[] = [];

          if (interResponse.success && interResponse.data?.taxes) {
            const activeInterTaxes = interResponse.data.taxes.filter(
              (tax: Tax) => tax.is_inactive === "0"
            );
            allTaxes.push(...activeInterTaxes);
          }

          if (intraResponse.success && intraResponse.data?.taxes) {
            const activeIntraTaxes = intraResponse.data.taxes.filter(
              (tax: Tax) => tax.is_inactive === "0"
            );
            allTaxes.push(...activeIntraTaxes);
          }

          // Remove duplicates based on tax_id
          const uniqueTaxes = allTaxes.filter(
            (tax, index, self) =>
              index === self.findIndex((t) => t.tax_id === tax.tax_id)
          );

          setAvailableTaxes(uniqueTaxes);
        } catch (err) {
          console.error("Failed to fetch taxes:", err);
          // Fallback to default taxes
          setAvailableTaxes(taxes);
        } finally {
          setTaxesLoading(false);
        }
        return;
      }

      // For existing credit notes, use initial tax_name to determine specification
      // Only fetch once when modal opens, not when tax is selected
      const taxName = editingLineItemData.tax_name || "";

      // If tax_name exists and starts with "I", it's IGST (inter-state)
      // Otherwise, it's GST (intra-state)
      const specification =
        taxName.trim().charAt(0).toUpperCase() === "I" ? "inter" : "intra";

      try {
        setTaxesLoading(true);
        const response = await fetchTaxSpecification(specification);

        if (response.success) {
          const activeTaxes = response.data.taxes.filter(
            (tax: Tax) => tax.is_inactive === "0"
          );
          setAvailableTaxes(activeTaxes);
        } else {
          // Fallback to default taxes
          setAvailableTaxes(taxes);
        }
      } catch (err) {
        console.error("Failed to fetch taxes:", err);
        // Fallback to default taxes
        setAvailableTaxes(taxes);
      } finally {
        setTaxesLoading(false);
      }
    };

    loadTaxes();
    // Only depend on open, isGstEnabled, and isManualCreation
    // Do NOT depend on tax_name to avoid re-fetching when tax is selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isGstEnabled, isManualCreation]);

  // Fetch TDS rates when modal opens
  useEffect(() => {
    const loadTDSRates = async () => {
      if (!open) {
        return;
      }

      try {
        setTdsLoading(true);
        const response = await fetchTDSRates();

        if (response.success && response.data?.tds_rates) {
          setTdsRates(response.data.tds_rates);
        } else {
          setTdsRates([]);
        }
      } catch (err) {
        console.error("Failed to fetch TDS rates:", err);
        setTdsRates([]);
      } finally {
        setTdsLoading(false);
      }
    };

    loadTDSRates();
  }, [open]);

  // Handle semantic override API call
  // Credit Note uses "credit_note" as document_type for semantic override
  const handleSemanticOverride = async () => {
    if (!editingLineItemData || !accountChangeReason.trim()) {
      setError("Please provide a reason for the account change");
      return;
    }
    if (!selectedAccount) {
      setError("Selected account not found");
      return;
    }

    // Check permissions based on change scope
    if (changeScope === "always") {
      if (!canOverridePerm) {
        setError(
          "You do not have permission to permanently override GL accounts"
        );
        setIsLoading(false);
        return;
      }

      // If review is required, check for approved review
      if (needsReview && creditNoteId) {
        try {
          const result = await checkApprovedReview({
            entity_type: "credit_note",
            entity_id:
              typeof creditNoteId === "string"
                ? parseInt(creditNoteId)
                : creditNoteId,
            action_code: ACTION_CODES.OVERRIDE_GL_PERMANENT,
          });

          if (!result.data.approved) {
            // Show review modal
            setPendingOverrideAction({
              action: async () => {
                // Continue with override after review is approved
                await performSemanticOverride();
              },
              changeDetails: {
                old_value: storedOriginalAccountName || originalAccountName,
                new_value: selectedAccount.gl_name,
                reason: accountChangeReason,
              },
            });
            setIsReviewModalOpen(true);
            return;
          }
        } catch (error) {
          console.error("Failed to check review status:", error);
          // If check fails, show review modal
          setPendingOverrideAction({
            action: async () => {
              await performSemanticOverride();
            },
            changeDetails: {
              old_value: storedOriginalAccountName || originalAccountName,
              new_value: selectedAccount.gl_name,
              reason: accountChangeReason,
            },
          });
          setIsReviewModalOpen(true);
          return;
        }
      }
    } else if (changeScope === "once") {
      if (!canOverrideOnce) {
        setError("You do not have permission to override GL accounts");
        setIsLoading(false);
        return;
      }
    }

    // Perform the override
    await performSemanticOverride();
  };

  // Separate function to perform the actual semantic override
  const performSemanticOverride = async () => {
    if (!selectedAccount) {
      setError("Selected account not found");
      setIsLoading(false);
      return;
    }
    if (!editingLineItemData) {
      setError("Line item data not found");
      setIsLoading(false);
      return;
    }

    // Store in local variable for type narrowing
    const account = selectedAccount;
    const lineItemData = editingLineItemData;

    setIsLoading(true);
    setError(null);
    try {
      const overrideData: SemanticOverrideRequest = {
        line_item: {
          vendor: "",
          vendor_id: "",
          description: lineItemData.description || "",
          product_description: lineItemData.description || "",
          hsn: lineItemData.hsn_or_sac || "",
          hsn_description: lineItemData.hsn_description || "",
          customer: customerName || "",
          customer_id: "",
        },
        gl_id: account.gl_id,
        similarity_threshold: 0.85,
        document_type: "credit_note", // Credit note uses "credit_note" for semantic override
        account_id: account.account_id,
        note: accountChangeReason,
      };
      const response = await createSemanticOverride(overrideData);
      if (response.success) {
        // Continue with normal save
        onSave();
      } else {
        throw new Error(
          response.message || "Failed to create semantic override"
        );
      }
    } catch (error) {
      console.error("Error creating semantic override:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create semantic override"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle account selection from AccountNameDropdown
  const handleAccountSelection = (account: GLMappingEntity) => {
    // Store full account entity in state for efficient handleSemanticOverride
    setSelectedAccount(account);

    const accountId = account.account_id;
    const updatedData = {
      ...editingLineItemData,
      account_name: account.gl_name,
      account_id: accountId,
    };
    onLineItemDataChange(updatedData);

    // For manual creation, do not show account change section
    if (isManualCreation) {
      setShowAccountChangeSection(false);
    } else if (isNewItem) {
      // For new items (in existing credit notes), show account change section
      setShowAccountChangeSection(true);
    } else {
      // For existing items, show only if the account name has changed
      const hasAccountChanged =
        storedOriginalAccountName &&
        storedOriginalAccountName !== account.gl_name &&
        storedOriginalAccountName.trim() !== "" &&
        account.gl_name.trim() !== "";

      if (hasAccountChanged) {
        setShowAccountChangeSection(true);
      } else {
        setShowAccountChangeSection(false);
      }
    }
  };

  // Handle tax selection from Select
  const handleTaxSelection = (selectedTaxId: string) => {
    const selectedTax = availableTaxes.find(
      (tax) => tax.tax_id === selectedTaxId
    );
    if (selectedTax) {
      const taxPercentage = parseFloat(selectedTax.tax_percentage);
      const itemTotal = editingLineItemData.item_total || 0;
      const taxAmount = (itemTotal * taxPercentage) / 100;

      // Ensure tax_name is set - use tax_name from tax object, or construct it if empty
      let taxName = selectedTax.tax_name;
      if (!taxName || taxName.trim() === "") {
        // Construct tax_name from tax_type and tax_percentage if tax_name is empty
        // Examples: IGST18, GST18, CGST9, SGST9
        const taxType = selectedTax.tax_type || "";
        const taxSpecificType = selectedTax.tax_specific_type || "";
        
        // If tax_id looks like a tax name (e.g., "IGST18"), use it
        if (selectedTax.tax_id && /^[A-Z]+(\d+)$/i.test(selectedTax.tax_id)) {
          taxName = selectedTax.tax_id.toUpperCase();
        } else if (taxSpecificType) {
          // Use tax_specific_type if available (e.g., "IGST", "CGST", "SGST")
          taxName = `${taxSpecificType.toUpperCase()}${Math.round(taxPercentage)}`;
        } else if (taxType) {
          // Use tax_type as fallback
          taxName = `${taxType.toUpperCase()}${Math.round(taxPercentage)}`;
        } else {
          // Final fallback: use tax_id
          taxName = selectedTax.tax_id || "";
        }
      }

      const updatedData = {
        ...editingLineItemData,
        tax_id: selectedTax.tax_id,
        tax_name: taxName,
        tax_percentage: taxPercentage,
        tax_amount: taxAmount,
        item_total_inclusive_of_tax: itemTotal + taxAmount,
      };
      onLineItemDataChange(updatedData);
    }
  };

  // Handle TDS selection from Select
  const handleTDSSelection = (selectedTdsId: string) => {
    const selectedTDS = tdsRates.find(
      (tds) => tds.tds_tax_id === selectedTdsId
    );
    if (selectedTDS) {
      const updatedData = {
        ...editingLineItemData,
        tds_tax_id: selectedTDS.tds_tax_id,
        tds_tax_name: selectedTDS.tds_tax_name,
        tds_tax_percentage: selectedTDS.tds_tax_percentage,
      };
      onLineItemDataChange(updatedData);
    }
  };

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    let updatedData = {
      ...editingLineItemData,
      [field]: value,
    };

    // Recalculate item_total, tax_amount, and tds_tax_amount when quantity or rate changes
    if (field === "quantity" || field === "rate") {
      const quantity =
        field === "quantity" ? value : editingLineItemData.quantity || 0;
      const rate = field === "rate" ? value : editingLineItemData.rate || 0;
      const itemTotal = quantity * rate;
      const taxPercentage = editingLineItemData.tax_percentage || 0;
      const taxAmount = (itemTotal * taxPercentage) / 100;

      // Calculate TDS amount if TDS is present (for vendor credits)
      let tdsTaxAmount = 0;
      if (
        editingLineItemData.tds_tax_id &&
        editingLineItemData.tds_tax_percentage
      ) {
        tdsTaxAmount =
          (itemTotal * editingLineItemData.tds_tax_percentage) / 100;
      }

      updatedData = {
        ...updatedData,
        item_total: itemTotal,
        tax_amount: isGstEnabled ? taxAmount : 0,
        tds_tax_amount: tdsTaxAmount,
        item_total_inclusive_of_tax: isGstEnabled
          ? itemTotal + taxAmount
          : itemTotal,
      };
    }

    onLineItemDataChange(updatedData);
  };

  // Validate form for new items
  const isFormValid = () => {
    if (!editingLineItemData) return false;

    const hasDescription = editingLineItemData.description?.trim().length > 0;
    const hasQuantity = editingLineItemData.quantity > 0;
    const hasRate = editingLineItemData.rate > 0;
    const hasAccount = editingLineItemData.account_id?.trim().length > 0;
    const hasTax = isGstEnabled
      ? editingLineItemData.tax_id?.trim().length > 0
      : true; // Tax is optional when GST is disabled
    // HSN/SAC is optional

    return hasDescription && hasQuantity && hasRate && hasAccount && hasTax;
  };

  // Handle save with API integration
  const handleSave = async () => {
    if (changeScope === "always" && showAccountChangeSection) {
      // Validate that reason is provided when "Change it always" is selected
      if (!accountChangeReason.trim()) {
        setError("Please provide a reason for the account change");
        return;
      }
      // Call semantic override API first
      await handleSemanticOverride();
    } else {
      // Normal save without API call
      onSave();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!editingLineItemData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[600px] max-w-[700px]">
        {/* Header */}
        <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Edit Line Item
            </span>
            <span className="text-body font-body text-subtext-color">
              Update the details for this line item
            </span>
          </div>
          <IconButton size="small" icon={<FeatherX />} onClick={handleCancel} />
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Description *
            </span>
            <TextField className="h-auto w-full flex-none" label="" helpText="">
              <TextField.Input
                placeholder="Enter line item description"
                value={editingLineItemData.description || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange("description", event.target.value)
                }
                disabled={true}
                readOnly={true}
              />
            </TextField>
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Quantity *"
              helpText=""
            >
              <TextField.Input
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.quantity?.toString() || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string and proper decimal input
                  const quantity = value === "" ? 0 : parseFloat(value) || 0;
                  handleFieldChange("quantity", quantity);
                }}
                onWheel={(event: React.WheelEvent<HTMLInputElement>) => {
                  // Prevent mouse wheel from changing the number input value
                  event.currentTarget.blur();
                }}
                disabled={false} // Quantity is always editable (even for vendor credits)
              />
            </TextField>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Rate *"
              helpText=""
            >
              <TextField.Input
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.rate?.toString() || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string and proper decimal input
                  const rate = value === "" ? 0 : parseFloat(value) || 0;
                  handleFieldChange("rate", rate);
                }}
                onWheel={(event: React.WheelEvent<HTMLInputElement>) => {
                  // Prevent mouse wheel from changing the number input value
                  event.currentTarget.blur();
                }}
                disabled={false} // Rate is always editable (even for vendor credits)
              />
            </TextField>
          </div>

          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex flex-col gap-1">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Account Name *
              </span>
              <AccountNameDropdown
                docType="invoice"
                selectedAccountId={editingLineItemData.account_id}
                selectedAccountName={editingLineItemData.account_name}
                onSelect={handleAccountSelection}
                disabled={true}
              />
            </div>
            {showAccountChangeSection && !isManualCreation && (
              <div className="flex w-full flex-col items-start gap-4 rounded-md bg-brand-100 px-4 py-4">
                {/* Only show the "changed from/to" warning for EXISTING items */}
                {!isNewItem && (
                  <div className="flex w-full items-start gap-3 rounded-md border border-solid border-warning-300 bg-warning-100 px-3 py-3">
                    <FeatherInfo className="text-body font-body text-warning-700" />
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Account name changed
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        From: {storedOriginalAccountName} → To:{" "}
                        {editingLineItemData.account_name}
                      </span>
                    </div>
                  </div>
                )}
                <TextArea
                  className="h-auto w-full flex-none"
                  label={`Reason for change${
                    changeScope === "always" ? " *" : ""
                  }`}
                  helpText={
                    changeScope === "always"
                      ? "Required when applying changes to all future line items"
                      : ""
                  }
                >
                  <TextArea.Input
                    placeholder="Enter reason for changing account name..."
                    value={accountChangeReason}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setAccountChangeReason(event.target.value)
                    }
                  />
                </TextArea>
                <div className="flex w-full items-start gap-4">
                  <div
                    className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                      !canOverrideOnce
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      changeScope === "once"
                        ? "bg-warning-800"
                        : "border border-solid border-neutral-border bg-default-background"
                    }`}
                    onClick={() => canOverrideOnce && setChangeScope("once")}
                  >
                    <div
                      className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 border-solid mt-0.5 ${
                        changeScope === "once"
                          ? "border-white"
                          : "border-neutral-400"
                      }`}
                    >
                      {changeScope === "once" && (
                        <div className="flex h-2 w-2 flex-none items-start rounded-full bg-white" />
                      )}
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span
                        className={`text-body-bold font-body-bold ${
                          changeScope === "once"
                            ? "text-white"
                            : "text-default-font"
                        }`}
                      >
                        Change this once
                      </span>
                      <span
                        className={`text-caption font-caption ${
                          changeScope === "once"
                            ? "text-white"
                            : "text-subtext-color"
                        }`}
                      >
                        Apply this change only to this specific line item
                      </span>
                    </div>
                  </div>
                  <div
                    className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                      !canOverridePerm
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      changeScope === "always"
                        ? "bg-warning-800"
                        : "border border-solid border-neutral-border bg-default-background"
                    }`}
                    onClick={() => canOverridePerm && setChangeScope("always")}
                  >
                    <div
                      className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 border-solid mt-0.5 ${
                        changeScope === "always"
                          ? "border-white"
                          : "border-neutral-400"
                      }`}
                    >
                      {changeScope === "always" && (
                        <div className="flex h-2 w-2 flex-none items-start rounded-full bg-white" />
                      )}
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span
                        className={`text-body-bold font-body-bold ${
                          changeScope === "always"
                            ? "text-white"
                            : "text-default-font"
                        }`}
                      >
                        Change it always
                      </span>
                      <span
                        className={`text-caption font-caption ${
                          changeScope === "always"
                            ? "text-white"
                            : "text-subtext-color"
                        }`}
                      >
                        Apply this change to all future line items with this
                        description
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="HSN/SAC Code"
              helpText=""
            >
              <TextField.Input
                placeholder="48025790"
                value={editingLineItemData.hsn_or_sac || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange("hsn_or_sac", event.target.value)
                }
                disabled={true}
                readOnly={true}
              />
            </TextField>
            {isGstEnabled && (
              <Select
                className="h-auto grow shrink-0 basis-0"
                label="Tax Rate (%) *"
                helpText={
                  taxesLoading
                    ? "Loading taxes..."
                    : editingLineItemData.tax_name
                    ? `${editingLineItemData.tax_name} (${editingLineItemData.tax_percentage}%)`
                    : "No tax selected"
                }
                value={editingLineItemData.tax_id || ""}
                onValueChange={handleTaxSelection}
                placeholder={
                  taxesLoading ? "Loading taxes..." : "Select tax rate"
                }
                disabled={true}
              >
                {availableTaxes.map((tax) => (
                  <Select.Item key={tax.tax_id} value={tax.tax_id}>
                    {tax.tax_name} ({tax.tax_percentage}%)
                  </Select.Item>
                ))}
              </Select>
            )}
          </div>

          {/* TDS Selection */}
          <div className="flex w-full items-start gap-6 flex-1">
            <Select
              className="h-auto grow shrink-0 basis-0"
              label="TDS Rate (Optional)"
              helpText={
                tdsLoading
                  ? "Loading TDS rates..."
                  : editingLineItemData.tds_tax_id &&
                    editingLineItemData.tds_tax_percentage
                  ? `${editingLineItemData.tds_tax_name || "TDS"} (${
                      editingLineItemData.tds_tax_percentage
                    }%)`
                  : "No TDS selected"
              }
              value={editingLineItemData.tds_tax_id || ""}
              onValueChange={handleTDSSelection}
              placeholder={
                tdsLoading ? "Loading TDS rates..." : "Select TDS rate"
              }
              disabled={true}
            >
              {tdsRates.map((tds) => (
                <Select.Item key={tds.tds_tax_id} value={tds.tds_tax_id}>
                  {tds.tds_tax_name} ({tds.tds_tax_percentage}%)
                </Select.Item>
              ))}
            </Select>
          </div>

          <div className="flex w-full items-center justify-between rounded-md bg-success-50 px-4 py-3">
            <span className="text-body-bold font-body-bold text-default-font">
              Item Total{" "}
              {isGstEnabled && (editingLineItemData.tax_percentage || 0) > 0
                ? "(including GST)"
                : ""}{" "}
              {editingLineItemData.tds_tax_id ? "(after TDS)" : ""}
            </span>
            <span className="text-heading-3 font-heading-3 text-success-600">
              ₹
              {(() => {
                const subtotal = editingLineItemData.item_total || 0;
                const taxAmount =
                  isGstEnabled && (editingLineItemData.tax_percentage || 0) > 0
                    ? (subtotal * (editingLineItemData.tax_percentage || 0)) /
                      100
                    : 0;
                const tdsAmount =
                  editingLineItemData.tds_tax_id &&
                  editingLineItemData.tds_tax_percentage
                    ? (subtotal * editingLineItemData.tds_tax_percentage) / 100
                    : 0;
                // Total = Subtotal + GST - TDS (TDS is deducted)
                return roundOff(subtotal + taxAmount - tdsAmount);
              })()}
            </span>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex w-full flex-col items-start gap-2 px-6">
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-300 bg-error-100 px-3 py-3">
              <FeatherInfo className="text-body font-body text-error-700" />
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-error-700">
                  Error
                </span>
                <span className="text-body font-body text-error-700">
                  {error}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Footer */}
        <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <Button
            variant="neutral-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            onClick={handleSave}
            disabled={
              isLoading ||
              (isNewItem && !isFormValid()) ||
              (changeScope === "always" &&
                showAccountChangeSection &&
                !accountChangeReason.trim())
            }
          >
            {isLoading
              ? isNewItem
                ? "Adding..."
                : "Saving..."
              : isNewItem
              ? "Add Item"
              : "Save Changes"}
          </Button>
        </div>
      </Dialog.Content>

      {/* Review Request Modal */}
      {creditNoteId && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.CREDIT_NOTE}
          actionCode={ACTION_CODES.OVERRIDE_GL_PERMANENT}
          entityType="credit_note"
          entityId={
            typeof creditNoteId === "string"
              ? parseInt(creditNoteId)
              : creditNoteId
          }
          changeDetails={pendingOverrideAction?.changeDetails}
          onSuccess={async () => {
            // Refresh review status after successful review request
            if (needsReview && creditNoteId) {
              try {
                const result = await checkApprovedReview({
                  entity_type: "credit_note",
                  entity_id:
                    typeof creditNoteId === "string"
                      ? parseInt(creditNoteId)
                      : creditNoteId,
                  action_code: ACTION_CODES.OVERRIDE_GL_PERMANENT,
                });
                setHasApprovedReview(result.data.approved);
                // If approved, execute the pending override action
                if (result.data.approved && pendingOverrideAction) {
                  await pendingOverrideAction.action();
                }
              } catch (error) {
                console.error("Failed to check review status:", error);
              }
            }
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setError(
              error instanceof Error
                ? error.message
                : "Failed to create review request"
            );
          }}
        />
      )}
    </Dialog>
  );
};

export { EditLineItemForCreditNoteComponent };
export default EditLineItemForCreditNoteComponent;
